import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { projectsAPI } from '../lib/api';
import { trackEvent } from '../lib/analytics';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  ArrowLeft, 
  Plus, 
  X, 
  Loader2,
  AlertCircle,
  Sparkles,
  CalendarIcon
} from 'lucide-react';
import { cn } from '../lib/utils';

export const CreateProjectPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    scope: '',
    teams: [],
    timeline: '',
    key_deliverables: [],
    start_date: null
  });
  
  const [teamInput, setTeamInput] = useState('');
  const [deliverableInput, setDeliverableInput] = useState('');

  const handleAddTeam = () => {
    if (teamInput.trim() && !formData.teams.includes(teamInput.trim())) {
      setFormData({ ...formData, teams: [...formData.teams, teamInput.trim()] });
      setTeamInput('');
    }
  };

  const handleRemoveTeam = (team) => {
    setFormData({ ...formData, teams: formData.teams.filter(t => t !== team) });
  };

  const handleAddDeliverable = () => {
    if (deliverableInput.trim() && !formData.key_deliverables.includes(deliverableInput.trim())) {
      setFormData({ 
        ...formData, 
        key_deliverables: [...formData.key_deliverables, deliverableInput.trim()] 
      });
      setDeliverableInput('');
    }
  };

  const handleRemoveDeliverable = (deliverable) => {
    setFormData({ 
      ...formData, 
      key_deliverables: formData.key_deliverables.filter(d => d !== deliverable) 
    });
  };

  const handleSubmit = async (e, generatePlan = false) => {
    e.preventDefault();
    setError('');

    if (!formData.name || !formData.scope || !formData.timeline) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.teams.length === 0) {
      setError('Please add at least one team');
      return;
    }

    if (formData.key_deliverables.length === 0) {
      setError('Please add at least one key deliverable');
      return;
    }

    setLoading(true);
    if (generatePlan) setGenerating(true);

    try {
      const payload = {
        ...formData,
        start_date: formData.start_date ? format(formData.start_date, 'yyyy-MM-dd') : null
      };
      
      const res = await projectsAPI.create(payload);
      const projectId = res.data.id;
      
      // Track project creation
      trackEvent('project_created', { project_id: projectId });

      if (generatePlan) {
        await projectsAPI.generatePlan(projectId);
        // Track plan generation
        trackEvent('plan_generated', { project_id: projectId });
      }

      navigate(`/projects/${projectId}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create project');
      setLoading(false);
      setGenerating(false);
    }
  };

  const teamSuggestions = ['Engineering', 'Product', 'Design', 'QA', 'Business', 'Vendor', 'Marketing', 'Legal'];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 animate-fade-in">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="mb-4"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="font-heading text-3xl font-bold text-foreground">
            Create New Project
          </h1>
          <p className="text-muted-foreground mt-1">
            Define your project scope and let AI generate an execution plan
          </p>
        </div>

        {/* Form */}
        <Card className="border-slate-200 animate-fade-in stagger-1">
          <CardHeader>
            <CardTitle className="font-heading">Project Details</CardTitle>
            <CardDescription>
              Provide comprehensive information for better AI analysis
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-6">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md text-sm" data-testid="create-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              {/* Project Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Project Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., Mobile App Launch Q2"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="project-name-input"
                />
              </div>

              {/* Scope */}
              <div className="space-y-2">
                <Label htmlFor="scope">Project Scope *</Label>
                <Textarea
                  id="scope"
                  placeholder="Describe the project scope in detail. Include objectives, requirements, constraints, and any relevant context..."
                  value={formData.scope}
                  onChange={(e) => setFormData({ ...formData, scope: e.target.value })}
                  rows={6}
                  required
                  data-testid="project-scope-input"
                />
                <p className="text-xs text-muted-foreground">
                  The more detail you provide, the better the AI can generate your execution plan
                </p>
              </div>

              {/* Start Date and Timeline Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Start Date */}
                <div className="space-y-2">
                  <Label>Project Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.start_date && "text-muted-foreground"
                        )}
                        data-testid="start-date-picker"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.start_date ? format(formData.start_date, 'PPP') : 'Select start date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.start_date}
                        onSelect={(date) => setFormData({ ...formData, start_date: date })}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Timeline */}
                <div className="space-y-2">
                  <Label htmlFor="timeline">Timeline *</Label>
                  <Input
                    id="timeline"
                    placeholder="e.g., Q2 2024, 3 months"
                    value={formData.timeline}
                    onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
                    required
                    data-testid="project-timeline-input"
                  />
                </div>
              </div>

              {/* Teams */}
              <div className="space-y-2">
                <Label>Teams Involved *</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add team name"
                    value={teamInput}
                    onChange={(e) => setTeamInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTeam();
                      }
                    }}
                    data-testid="team-input"
                  />
                  <Button type="button" variant="outline" onClick={handleAddTeam} data-testid="add-team-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {teamSuggestions.filter(t => !formData.teams.includes(t)).slice(0, 4).map(team => (
                    <Badge 
                      key={team} 
                      variant="outline" 
                      className="cursor-pointer hover:bg-slate-100"
                      onClick={() => setFormData({ ...formData, teams: [...formData.teams, team] })}
                    >
                      + {team}
                    </Badge>
                  ))}
                </div>
                {formData.teams.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {formData.teams.map(team => (
                      <Badge key={team} className="bg-primary text-white">
                        {team}
                        <button
                          type="button"
                          onClick={() => handleRemoveTeam(team)}
                          className="ml-1.5 hover:text-red-200"
                          data-testid={`remove-team-${team}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Key Deliverables */}
              <div className="space-y-2">
                <Label>Key Deliverables *</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add deliverable"
                    value={deliverableInput}
                    onChange={(e) => setDeliverableInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddDeliverable();
                      }
                    }}
                    data-testid="deliverable-input"
                  />
                  <Button type="button" variant="outline" onClick={handleAddDeliverable} data-testid="add-deliverable-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {formData.key_deliverables.length > 0 && (
                  <div className="space-y-2 mt-3">
                    {formData.key_deliverables.map((deliverable, index) => (
                      <div 
                        key={index} 
                        className="flex items-center gap-2 p-2 bg-slate-50 rounded-md"
                      >
                        <span className="flex-1 text-sm">{deliverable}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveDeliverable(deliverable)}
                          className="text-muted-foreground hover:text-destructive"
                          data-testid={`remove-deliverable-${index}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-200">
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={(e) => handleSubmit(e, false)}
                  disabled={loading}
                  data-testid="save-project-btn"
                >
                  {loading && !generating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Save Project
                </Button>
                <Button 
                  type="button"
                  onClick={(e) => handleSubmit(e, true)}
                  disabled={loading}
                  data-testid="generate-plan-btn"
                >
                  {generating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  {generating ? 'Generating Plan...' : 'Generate Execution Plan'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};
